import { NonServicePeriod } from "src/app/types/non-service-period.type";
import { BuildServices, GenericRouteEvent, RouteEventAction, RouteEventActionColorMap, RouteEventActionFillMap, RouteEventActionTypeMap } from "./generic-route-event.model";
import { DateTime } from "luxon";
import * as helpers from '../../helpers/ui-helpers';
import { BaseJob, buildJobPerform } from "../job.type";

export class _NonServicePeriod extends GenericRouteEvent {

  public value: NonServicePeriod | BaseJob;
  public type = 'non-service-period';

  public nonServicePeriods: NonServicePeriod;
  public baseJob: BaseJob;

  constructor(
    data: NonServicePeriod | BaseJob,
    services: BuildServices,
  ) {
    super(services);
    this.value = data;

    this.nonServicePeriods = <NonServicePeriod>data;
    this.baseJob = <BaseJob>data;

    const { sequence } = data;
    const { hasBeenCompleted, hasBeenStarted, scheduledStart, scheduledEnd } = this.nonServicePeriods;
    const { location, stopDuration, scheduledTime, arriveTime, departedTime } = this.baseJob;

    this.started = !!arriveTime || hasBeenStarted;
    this.completed = !!departedTime || hasBeenCompleted;
    this.location = location;

    const _scheduledStart = scheduledStart || scheduledTime;
    this.scheduledStart = _scheduledStart ? DateTime.fromJSDate(new Date(_scheduledStart)) : null;

    this.scheduledEnd = scheduledEnd
      ? DateTime.fromJSDate(new Date(scheduledEnd))
      : this.scheduledStart && stopDuration
        ? this.scheduledStart.plus({ seconds: stopDuration })
        : null;

    this.headerInfo = {
      time: helpers.getTimeForCardHeader({ start: this.scheduledStart, end: this.scheduledEnd, translateSvc: services.translateSvc }),
      type: '',
      details: '',
      timeTo: this.scheduledStart?.toRelative(),
    };

    this.stopSequence = sequence || 0;
    this.actions = this.getActions();
    this.updateTimeBasedProperties();
  }

  public updateTimeBasedProperties = () => {
    const timeToDiff = this.started ? this.scheduledStart : this.scheduledEnd;
    this.isLate = this.completed ? false : helpers.isInThePast(timeToDiff);
    const actions = this.getActions();
    if (JSON.stringify(actions) !== JSON.stringify(this.actions)) {
      this.actions = actions;
    }
    this.headerInfo.timeTo = this.scheduledStart?.toRelative({ unit: [ 'hours', 'minutes'  ], padding: 6000 });
  };

  public getActions(): RouteEventAction[] {
    return [
      {
        show: this.started && !this.completed,
        type: RouteEventActionTypeMap.perform,
        label: this.translateSvc.translate('ACTION.finish'),
        icon: 'checkmark-circle-outline',
        color: RouteEventActionColorMap.primary,
        fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: (action: RouteEventAction) => this.end(action),
      },
      {
        show: !this.started,
        type: RouteEventActionTypeMap.perform,
        label: this.translateSvc.translate('ACTION.start'),
        icon: 'alarm',
        color: RouteEventActionColorMap.primary,
        fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: (action: RouteEventAction) => this.start(action),
      },
    ].filter(action => action.show);

  }

  protected async start(action?: RouteEventAction) {
    try {
      if (action) action.disabled = true;
      if (this.baseJob.stopType) {
        const payload = buildJobPerform({ job: this.baseJob, data: this.appData, signature: null, signatureReason: null, noShowReason: null });
        await this.routeProviderSvc.performJob(payload);
        this.baseJob.arriveTime = DateTime.now().toString();
        this.appData.setJobs([ this.baseJob ]);
      } else {
        await this.routeProviderSvc.startNonServicePeriod(this.nonServicePeriods.nonServicePeriodType);
        this.nonServicePeriods.hasBeenStarted = true;
      }
      this.started = true;
      this.actions = this.getActions();
    } catch (error) {
      this.notificationSvc.error(error?.message || error);
    } finally {
      if (action) action.disabled = false;
    }
  };

  protected async end(action: RouteEventAction) {
    try {
      action.disabled = true;
      if (this.baseJob.stopType) {
        const payload = buildJobPerform({job: this.baseJob, data: this.appData, signature: null, signatureReason: null, noShowReason: null });
        await this.routeProviderSvc.performJob(payload);
        this.baseJob.departedTime = DateTime.now().toString();
        this.baseJob.completed = true;
        this.appData.setJobs([ this.baseJob ]);
      } else {
        await this.routeProviderSvc.finishNonServicePeriod(this.nonServicePeriods.nonServicePeriodType);
        this.nonServicePeriods.hasBeenCompleted = true;
      }
      this.completed = true;
    } catch (error) {
      this.notificationSvc.error(error?.message || error);
    } finally {
      action.disabled = false;
    }
  };

}
